import { useBoUsers } from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { RbacAccessDenied } from '~/components/routes/administration/roles/access-denied'
import { ConfirmResourceDeletionButton } from '~/components/communication/ConfirmDeletionButton'
import { useToast } from '~/components/communication/Toast'
import PlusIcon from '~/components/icons/PlusIcon'
import { boUsersColumns } from '~/components/routes/users/users-columns'
import { UsersPageLayout, UsersSearchGrid } from '~/components/routes/users/users-page-layout'
import { useDeleteSelectedUsers } from '~/components/routes/users/use-delete-selected-users'
import { useRights } from '~/utils/hooks'
import { AuthorizedButtonLink, withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/users/bo')({
  staticData: {
    permission: ['read', 'users'],
  },
  component: BoUsersPage,
})
withRoutePermissions(Route)

function BoUsersPage() {
  const isUserSuperAdmin = useRights(['SUPER_ADMIN'])
  const isAdmin = useRights(['ADMIN'])

  const [Toast, showToast] = useToast()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<number[]>([])

  const { data: boUsers, refetch, isLoading } = useBoUsers()

  const users = useMemo(() => boUsers ?? [], [boUsers])

  const rows = useMemo(
    () =>
      users.filter((user) =>
        includesLowerCase(`${user.firstName} ${user.lastName} ${user.email}`, search),
      ),
    [users, search],
  )

  const handleDelete = useDeleteSelectedUsers(users, selected, showToast, () => {
    setSelected([])
    return refetch()
  })

  // La liste des utilisateurs du BO est réservée aux super admins : la route est une URL
  // à part entière, donc on refuse l'accès direct (l'onglet est déjà masqué côté navigation).
  if (!isUserSuperAdmin) {
    return <RbacAccessDenied />
  }

  return (
    <UsersPageLayout
      activeTab="bo"
      actions={
        <>
          <AuthorizedButtonLink
            variant="contained"
            sx={{ marginRight: '16px' }}
            to="/users/admins/new"
            disabled={!isUserSuperAdmin}
          >
            <PlusIcon />
            Ajouter un utilisateur
          </AuthorizedButtonLink>
          <AuthorizedButtonLink
            variant="contained"
            sx={{ marginRight: '16px' }}
            to="/users/admins/$id/edit"
            params={{ id: selected[0] ?? 0 }}
            disabled={!isUserSuperAdmin || selected.length !== 1}
          >
            Modifier
          </AuthorizedButtonLink>
          {isAdmin ? (
            <ConfirmResourceDeletionButton
              disabled={selected.length === 0}
              handleResourceDeletion={handleDelete}
            />
          ) : null}
        </>
      }
    >
      <Toast />
      <UsersSearchGrid
        search={search}
        onSearchChange={setSearch}
        columns={boUsersColumns}
        rows={rows}
        loading={isLoading}
        onRowSelectionModelChange={setSelected}
      />
    </UsersPageLayout>
  )
}
